import { Typography, useToast } from "@codat/orchard-ui";
import { useQueryClient } from "@tanstack/react-query";
import { useDeleteCompanyMutation } from "api/public";
import { companyKeys } from "hooks/query/useGetCompany";
import React from "react";
import getTryCatchErrorMessage from "utils/getTryCatchErrorMessage";

import Button from "components/Button";
import Modal from "components/Modal";
import ModalBody from "components/ModalBody";

import styles from "./DeleteCompanyModal.module.css";

interface DeleteCompanyModalProps {
  open: boolean;
  company: {
    id: string;
    name: string;
  };
  onCancel: () => void;
  onSuccess: (companyId: string) => void;
}
const DeleteCompanyModal: React.FC<DeleteCompanyModalProps> = ({
  open,
  company,
  onCancel,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const [{ loading }, deleteCompany] = useDeleteCompanyMutation({
    companyId: company.id,
  });

  const { addToast } = useToast();

  const handleDeleteClick = () => {
    deleteCompany()
      .then(() => {
        addToast({
          message: `Deleted company ${company.name}`,
          variant: "success",
        });
        queryClient.setQueryData(companyKeys.one(company.id), () => null);
        onSuccess(company.id);
      })
      .catch((error) => {
        addToast({
          message: `Unable to delete company: ${getTryCatchErrorMessage(
            error
          )}`,
          variant: "error",
        });
      });
  };

  return (
    <Modal
      onClose={onCancel}
      isPinned
      eventProperties={{ companyId: company.id }}
    >
      {open && company && (
        <ModalBody
          size="medium"
          title="Confirm delete"
          className={styles.card}
          data-testid="delete-company-modal"
          onClose={onCancel}
        >
          <Typography
            data-testid="delete-company-modal-body"
            variant="body_md"
            as="p"
          >
            Are you sure you want to delete {company.name}?
          </Typography>
          <div className={styles.footerContainer}>
            <Button
              data-testid="cancel-button"
              label="Cancel"
              onClick={onCancel}
              buttonName="Cancel"
              eventProperties={{ companyId: company.id }}
            ></Button>
            <Button
              data-testid="confirm-button"
              className={styles.submitButton}
              color="negative"
              label="Delete"
              onClick={handleDeleteClick}
              isLoading={loading}
              buttonName="Delete"
              eventProperties={{ companyId: company.id }}
            ></Button>
          </div>
        </ModalBody>
      )}
    </Modal>
  );
};

export default DeleteCompanyModal;
